import { vec2 } from "@tlaukkan/tsm";
import { Entity, Not, System } from "ecsy";
import { calculateTargetLead, claimVec2 } from "../box2dUtils";
import { AimForDirection, AimForPlayer, AimForPosition, AimForTarget, AimOrbit, Antibody, Chained, Chainer, ConsumableType, Consumer, Dead, ElementInfo, LaneInfo, LowPriorityTarget, Moved, NoTargetBehavior, OnNearTarget, Ownership, Physics, ReturningHome, Sentry, Spawner, Submerged, Sync, Targeting, TorqueOrientationControl, Transform, Virus, VisibleRange } from "../components";
import { getEntityPlayer } from "../composition";
import { BehaviorsConstants, CollisionCategory, PhysicsConstants, Speeds, VirusSpawnerConstants } from "../constants";
import { getEntityById } from "../entityFactory";
import { Game } from "../game";
import { triggerEffect } from "../triggeredEffects";
import { addComponent, angleFromUp, degToRad, getOrAddComponent, lineFrom, perpendicular, removeComponentIfPresent, rotateAround, smallestAngleBetween, uniformDistrubtion, vecToStr } from "../utils";

export class AimSystem extends System {

	game: Game;

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
	}

	execute(delta: number, time: number) {
		if (!this.game.inSimulation) return;
		//Antibodies that just go straight
		this.queries.antibodyStraightShots.added?.forEach(entity => {
			const owner = entity.getComponent(Ownership);
			const shooter = entity.getMutableComponent(AimForPosition);
			if (!owner) return; //TODO: Bug! Sometimes I get the destroyed shop state versions here. Why these and not others?
			if (!shooter) return; //TODO: Bug here most likely with bullets getting creating/destroyed same frame
			const player = this.game.players.find(player => player.id === owner.playerId);

			if (!shooter.target && player) {
				shooter.target = new vec2(player.spawnPoint);
			}
		});

		//Direction

		//Determine the active lane for sentries
		const activeLaneByPlayer = new Map<number, number>();
		if (this.queries.sentryAiming.results.length > 0) {

			//Count up viruses in all lanes for every player
			const countPerPlayerPerLane = new Map<number, number[]>();
			for (const virus of this.queries.liveVirusesByLane.results) {
				const owner = virus.getComponent(Ownership);
				const laneInfo = virus.getComponent(LaneInfo);
				const lane = laneInfo.lane;
				if (!countPerPlayerPerLane.has(owner.playerId)) {
					countPerPlayerPerLane.set(owner.playerId, new Array<number>(VirusSpawnerConstants.LaneCount).fill(0));
				}
				countPerPlayerPerLane.get(owner.playerId)![lane]++;
			};

			for (const player of this.game.players) {
				let activeLane = -1;
				//If there are live viruses, the lane with the most live viruses is active lane
				const virusCountPerLane = countPerPlayerPerLane.get(player.id);
				if (virusCountPerLane) {
					const numberOfVirusesInMostActiveLane = Math.max(...virusCountPerLane);
					if (numberOfVirusesInMostActiveLane > 0) {
						activeLane = virusCountPerLane.indexOf(numberOfVirusesInMostActiveLane);
					}
				}

				//If no live viruses, use the lane with the most unspawned viruses
				if (activeLane === -1) {
					for (let lane = 0; lane < VirusSpawnerConstants.LaneCount; lane++) {
						const cellsInLane = this.game.getVirusLanes(player)[lane]?.cells || [];
						const stillSpawning = cellsInLane.some(cell => {
							const spawner = getEntityById(cell.id)?.getComponent(Spawner);
							return spawner && spawner.amount > 0;
						});
						if (stillSpawning) {
							activeLane = lane;
							break;
						}
					}
				}

				//Default to lane 0 in case of tiebreaker
				if (activeLane === -1) {
					activeLane = 0;
				}
				activeLaneByPlayer.set(player.id, activeLane);
			}
		}

		//..then apply it per-sentry
		this.queries.sentryAiming.results.forEach(entity => {
			const sentry = entity.getComponent(Sentry);
			const transform = entity.getComponent(Transform);
			const orientationControl = entity.getComponent(TorqueOrientationControl);
			const physics = entity.getComponent(Physics);

			const player = getEntityPlayer(entity, this.game);
			if (!player) return;

			const activeLane = activeLaneByPlayer.get(player.id) ?? 0;
			let targetLaneCenter = this.game.getVirusLanes(player)[activeLane]?.center || transform.position;

			if (sentry.distance === undefined) {
				if (sentry.orbitOwner) {
					sentry.target = new vec2(player.bloodCellCenterPoint);
				}
				sentry.distance = vec2.distance(sentry.target, transform.position);
			}

			const toTarget = lineFrom(sentry.target, transform.position);
			const orbitPoint = toTarget.normalize()
				.scale(sentry.distance)
				.add(sentry.target);

			const toSelfLine = transform.position.copy().subtract(sentry.target);
			const toTargetLane = targetLaneCenter.copy().subtract(sentry.target);

			const currentAngle = angleFromUp(toSelfLine);
			const targetAngle = angleFromUp(toTargetLane);
			//Note: this gets 0 if orbitReferencePoint not set, so when nothing else to do we stay still
			const diff = smallestAngleBetween(currentAngle, targetAngle);

			orientationControl.fixedDirection = lineFrom(transform.position, targetLaneCenter);


			const distanceThreshold = physics.size.x / 2;
			const angleThreshold = distanceThreshold / Math.max(sentry.distance, distanceThreshold); //distance = radius * angle

			if (Math.abs(diff) > angleThreshold) {
				const step = Math.sign(diff) * Math.min(Math.abs(diff), Math.PI / 100);
				rotateAround(orbitPoint, step, sentry.target, orbitPoint);
				transform.direction = orbitPoint
					.subtract(transform.position)
					.normalize();
				physics.body.SetLinearDamping(0);
			}
			else {
				transform.direction = vec2.zero.copy();
				//TODO: We increased the speed and this works well for a quick stop, but at 
				//this speed it kind of overshoots the orbit constantly
				physics.body.SetLinearDamping(sentry.maxSpeed / distanceThreshold);
			}
		});

		//Orbit
		//Commented this out because aim system only runs during simulation. During shop overlay system handles this
		//TODO: When we have better shop/simulation switching maybe changes this to be always handled in this one place
		// this.queries.movedOrbiter.results.forEach(entity => {
		// 	const orbit = entity.getComponent(AimOrbit);
		// 	(orbit.distance as any) = undefined; //just..reset this when moved
		// });
		this.queries.orbiter.results.forEach(entity => {
			const orbit = entity.getComponent(AimOrbit);
			const transform = entity.getComponent(Transform);

			if (orbit.distance === undefined) {
				if (orbit.orbitOwner) {
					const player = getEntityPlayer(entity, this.game);
					if (!player) return;
					orbit.target = new vec2(player.bloodCellCenterPoint);
				}
				orbit.distance = vec2.distance(orbit.target, transform.position);
			}

			const toTarget = lineFrom(orbit.target, transform.position);
			const orbitPoint = toTarget.normalize()
				.scale(orbit.distance)
				.add(orbit.target);

			rotateAround(orbitPoint, Math.PI / 100, orbit.target, orbitPoint);

			transform.direction = orbitPoint
				.subtract(transform.position)
				.normalize();
		});

		// this.queries.spinner.results.forEach(entity => {
		// 	const orbit = entity.getComponent(AimOrbit);
		// 	const chain = entity.getComponent(Chained);
		// 	const transform = entity.getComponent(Transform);

		// 	if (chain.links.length > 0) {
		// 		const targetEntity = chain.links[0];
		// 		const targetTransform = targetEntity.getComponent(Transform);
		// 		orbit.target = targetTransform.position;
		// 		orbit.distance = vec2.distance(orbit.target, transform.position);
		// 	}
		// 	else {
		// 		return;
		// 	}

		// 	const toTarget = lineFrom(orbit.target, transform.position);
		// 	const orbitPoint = toTarget.normalize()
		// 		.scale(orbit.distance)
		// 		.add(orbit.target);
		// 	rotateAround(orbitPoint, Math.PI / 12, orbit.target, orbitPoint);

		// 	transform.direction = orbitPoint
		// 		.subtract(transform.position)
		// 		.normalize();
		// });

		this.queries.straight.results.forEach(entity => {
			const behavior = entity.getMutableComponent(AimForPosition);
			const transform = entity.getComponent(Transform);

			const position = transform.position.copy();
			const direction = lineFrom(position, behavior.target);

			if (behavior.error > 0) {
				const sync = entity.getComponent(Sync);
				const perpendicularVec = perpendicular(direction);
				const errorScaler = sync.rng.unitScaler() * behavior.error;
				direction.add(perpendicularVec.scale(errorScaler));
			}
			if (!behavior.moveTowardsTarget) {
				direction.negate();
			}

			transform.direction = direction;
		});

		this.queries.directional.results.forEach(entity => {
			const transform = entity.getMutableComponent(Transform);
			const direction = entity.getComponent(AimForDirection);
			if (direction.fixedDirection) {
				transform.direction = direction.fixedDirection;
			}
			else {
				transform.direction = transform.forward;
			}
		});

		const removeAimPlayer = new Array<Entity>();
		this.queries.playerTarget.results.forEach(entity => {
			const aim = entity.getMutableComponent(AimForPlayer);
			const sync = entity.getComponent(Sync);
			const player = this.game.players.find(player => player.id === aim.playerId);

			//Nullify target blood cell if it died, and we care to update targets more than once
			if (aim.target && !aim.onlyTargetOnce) {
				if (aim.target.hasComponent(Dead) || aim.target.hasComponent(Submerged) || !aim.target.hasComponent(Transform)) {
					aim.target = null;
				}
			}

			//Try to obtain a new target if we don't have one
			if (player && !aim.target && player.hpCells.length > 0) {
				const random = sync.rng.next();
				const idx = Math.floor(random * player.hpCells.length);
				const cellComp = player.hpCells[idx];
				const cell = getEntityById(cellComp.id);
				aim.target = cell || null;
			}

			//Point at the target
			if (aim.target && aim.target.hasComponent(Transform)) {
				const transform = entity.getMutableComponent(Transform);
				const targetTranfsorm = aim.target.getComponent(Transform);
				transform.direction = lineFrom(transform.position, targetTranfsorm.position);
				if (aim.disableAfterOneAim) {
					removeAimPlayer.push(entity);
				}
			}
		});
		removeAimPlayer.forEach(entity => entity.removeComponent(AimForPlayer));

		//Low priority target fills in visible range's nearest in the event it hasn't seen anything
		//TODO: This was a terrible design decision, this should just be part of targeting filters and all
		this.queries.lowPriorityTarget.added?.forEach(entity => {
			const lowPriorityTarget = entity.getComponent(LowPriorityTarget);
			const targeting = getOrAddComponent(entity, Targeting);
			const range = entity.getComponent(VisibleRange);
			const specIndex = targeting.addSpec(range.type, range.range, lowPriorityTarget.category, range.range);
			lowPriorityTarget.specIndex = specIndex;
		});
		this.queries.lowPriorityTarget.removed?.forEach(entity => {
			const lowPriorityTarget = entity.getRemovedComponent(LowPriorityTarget);
			const targeting = entity.getComponent(Targeting);
			if (targeting) {
				targeting.disableSpec(lowPriorityTarget.specIndex);
			}
		});
		for (const entity of this.queries.lowPriorityTarget.results) {
			const range = entity.getComponent(VisibleRange);
			if (!range.targetFound) {

				const lowPriorityTarget = entity.getComponent(LowPriorityTarget);
				const targeting = getOrAddComponent(entity, Targeting);
				let targets = targeting.getTargets(lowPriorityTarget.specIndex);
				if ((lowPriorityTarget.element as number) !== -1) {
					targets = targets.filter(target => {
						const info = target.getComponent(ElementInfo);
						return info && info.type === lowPriorityTarget.element;
					});
				}
				if (lowPriorityTarget.consumerOf !== -1) {
					targets = targets
						.filter(cell => cell.hasComponent(Consumer)
							&& cell.getComponent(Consumer).types.includes(lowPriorityTarget.consumerOf as ConsumableType))
						.filter(cell => cell.hasComponent(Transform));
				}

				//Note that this system is shared so this target assignment here doesn't need to be synced.
				if (targets.length > 0) {
					range.nearest = targets[0];
				}
				else {
					range.nearest = null;
				}
			}
		}

		this.queries.seeker.added?.forEach(entity => {
			const seeker = entity.getMutableComponent(AimForTarget);
			seeker.homePosition = entity.getComponent(Transform).position.copy();
		});

		this.queries.seeker.results.forEach(entity => {
			const seeker = entity.getMutableComponent(AimForTarget);
			const transform = entity.getComponent(Transform);
			const visibility = entity.getComponent(VisibleRange);
			const nearest = visibility.nearest;

			if (seeker.target !== nearest) {
				seeker.target = nearest;
			}

			if (seeker.target) {
				//If target is still alive, seek to it..
				const targetPhys = seeker.target.getComponent(Physics);
				if (targetPhys) {
					const targetTransform = seeker.target.getComponent(Transform);
					if (!seeker.leadTarget) {
						const targetPos = targetTransform.position.copy();
						transform.direction = targetPos.subtract(transform.position).normalize();
						return;
					}
					const dest = calculateTargetLead(seeker.target, transform.position, targetPhys, targetTransform);
					transform.direction = dest.subtract(transform.position).normalize();

					seeker.returnHomeTimer = BehaviorsConstants.SeekerReturnHomeTimeout;
					removeComponentIfPresent(entity, ReturningHome);
				}
				//..if not flag us to get a new target
				else {
					seeker.target = null;
				}
			}
			// What to do if no target in sight
			if (!seeker.target) {
				switch (seeker.noTargetBehavior) {
					case NoTargetBehavior.StayStill:
						transform.direction = vec2.zero.copy();
						break;
					case NoTargetBehavior.ReturnHome:
						const wasWaiting = seeker.returnHomeTimer > 0;
						seeker.returnHomeTimer -= PhysicsConstants.StepRate;
						if (seeker.homePosition !== null && seeker.returnHomeTimer <= 0) {

							const toHome = seeker.homePosition.copy().subtract(transform.position);
							if (toHome.length() > 10) {
								transform.direction = toHome.normalize();
								if (wasWaiting) {
									addComponent(entity, ReturningHome);
								}
							}
							else {
								transform.direction = vec2.zero.copy();
							}
						}
						break;
				}
			}
		});


		for (const entity of this.queries.proximityTrigger.results) {
			const trigger = entity.getComponent(OnNearTarget);
			const transform = entity.getComponent(Transform);
			const targeting: AimForPlayer | AimForTarget = entity.getComponent(AimForPlayer) || entity.getComponent(AimForTarget);
			if (!targeting || !targeting.target) continue;

			const target = targeting.target.getComponent(Transform);
			if (!target) continue;
			trigger.currentDistance = vec2.distance(target.position, transform.position);
			if (trigger.currentDistance < trigger.thresholdDistance) {
				triggerEffect(trigger.effect, entity, targeting.target);
				trigger.thresholdDistance = Infinity; //only trigger once
			}
		}
	}
}

AimSystem.queries = {
	antibodyStraightShots: { components: [AimForPosition, Antibody, Ownership], listen: { added: true } },
	straight: { components: [Physics, Sync, AimForPosition] },
	orbiter: { components: [Physics, AimOrbit] },
	sentryAiming: { components: [Physics, Sentry, TorqueOrientationControl] },
	liveVirusesByLane: { components: [Virus, LaneInfo, Ownership] },
	movedOrbiter: { components: [Physics, AimOrbit, Moved] },
	//spinner: { components: [Physics, AimOrbit, Chained] }, //spinners not in use, this affects all chained orbiters
	directional: { components: [Physics, AimForDirection] },
	seeker: { components: [Physics, AimForTarget, VisibleRange], listen: { added: true } },
	playerTarget: { components: [Physics, Transform, AimForPlayer] },
	proximityTrigger: { components: [OnNearTarget, Transform] },
	lowPriorityTarget: { components: [VisibleRange, LowPriorityTarget], listen: { added: true, removed: true } },
};
